import { useState } from 'react';
import {
  calculateHashes,
  getContentType,
  getFileCategory,
  shouldIgnoreFile,
} from '../common/utils/fileUtils';
import { getWaveformData } from '../common/utils/audioPlayerUtils';
import { useIsElectron, useElectronAPI } from '../context/ElectronContext';
import { useProjectDirectories } from '../context/ProjectDirectoriesContext';
import { desktopBridge } from '../services/desktopBridge';
import { useBridgeRequired } from '../context/BridgeRequiredContext';
import { FileCategory, FileSyncStatus } from '../common/models/enums';
import { getProjectPath } from '../common/utils/projectPathUtils';
import { getFileFromPath } from '../common/utils/fileReadingUtils';
import {
  useProjectVersionPushMutation,
  useProjectVersionFinalizeMutation,
  useProjectVersionRollbackMutation,
} from '../generated/graphql';
import {
  detectPluginsFromFiles,
  pushVersionAndUpload,
} from '../common/utils/uploadUtils';
import { useTransferManagerContext } from '../context/TransferManagerContext';
import { assertUniqueFilePaths } from '../common/utils/projectManifestUtils';
import { reportTransferError } from '@/services/transferDiagnostics';
import { getRuntimeInfo } from '@/services/runtimeInfo';

// Updated type definitions for the new version control system
export interface ProjectVersionType {
  _id: string;
  version: number;
  commitMessage: string;
  deletedFiles: string[]; // Array of file IDs that were deleted in this version
  createdFiles: string[]; // Array of file IDs that were created in this version
  updatedFiles: string[]; // Array of file IDs that were updated in this version
  createdAt: string;
}

export interface ProjectFileType {
  _id: string;
  fileName: string;
  quickHash: string;
  fullHash: string;
  uploadedAt: string;
  size: number;
  contentType: string;
  s3Key: string;
  replacesFileId?: string; // ID of the file this file replaces (for updates)
}

export interface ProjectType {
  _id: string;
  name: string;
  files?: ProjectFileType[];
  versionHistory?: ProjectVersionType[];
}

export interface SyncStatus {
  syncing: boolean;
  progress: number;
  error?: string;
}

export interface FileStatus {
  fileName: string;
  status: FileSyncStatus;
  isLocal: boolean;
}

export interface SyncOperationResult {
  success: boolean;
  error?: string;
}

export function useProjectSync(
  project: ProjectType | null,
  projectDir: string | FileSystemDirectoryHandle | null,
  latestVersionFiles: ProjectFileType[] = [],
  onSyncComplete?: () => void
) {
  const [syncStatus, setSyncStatus] = useState<SyncStatus>({
    syncing: false,
    progress: 0,
  });
  const [projectVersionPush] = useProjectVersionPushMutation();
  const [projectVersionFinalize] = useProjectVersionFinalizeMutation();
  const [projectVersionRollback] = useProjectVersionRollbackMutation();
  const isElectron = useIsElectron();
  const electronAPI = useElectronAPI();
  const { getStoredProjectPath } = useProjectDirectories();
  const { requireBridge } = useBridgeRequired();
  const { enqueueSync } = useTransferManagerContext();

  // Early return if no project
  if (!project) {
    return {
      syncStatus: { syncing: false, progress: 0, error: 'No project selected' },
      checkStatus: async () => [],
      commitChanges: async () => ({
        success: false,
        error: 'No project selected',
      }),
    };
  }

  // Get files for the latest version
  const getLatestVersionFiles = (): ProjectFileType[] => {
    return latestVersionFiles;
  };

  // Helper function to get project path (string) from projectDir
  const getProjectPathValue = (): string | null => {
    return getProjectPath(
      projectDir,
      isElectron,
      project?._id || null,
      getStoredProjectPath
    );
  };

  // Get file from path (for sync operations - Electron only)
  const getFileFromPathValue = async (
    filePath: string
  ): Promise<File | null> => {
    const projectPath = getProjectPathValue();
    if (!projectPath || !isElectron || !electronAPI) {
      return null;
    }
    return getFileFromPath(filePath, projectPath, electronAPI, true);
  };

  // Calculate sync status (wrapper around utility)
  const checkStatus = async () => {
    const projectPath = getProjectPathValue();
    if (!projectPath && !projectDir) {
      setSyncStatus({
        syncing: false,
        progress: 0,
        error: 'No project directory selected',
      });
      return [];
    }

    try {
      setSyncStatus({ syncing: true, progress: 0 });

      // Get files from the latest version
      const latestVersionFiles = getLatestVersionFiles();

      // Filter out ignored files from cloud files before sync check
      const filteredCloudFiles = latestVersionFiles.filter(
        (file) => !shouldIgnoreFile(file.fileName)
      );

      // Electron mode: Use Electron sync manager
      if (isElectron && electronAPI && project?._id && projectPath) {
        const result = await electronAPI.requestSyncCheck(
          project._id,
          projectPath,
          filteredCloudFiles
        );

        if (result.success && result.fileStatuses) {
          setSyncStatus({ syncing: false, progress: 100 });
          if (onSyncComplete) {
            onSyncComplete();
          }
          return result.fileStatuses;
        } else {
          throw new Error(result.error || 'Failed to check sync status');
        }
      }

      // Web mode: Query desktop bridge
      if (!isElectron && project?._id && projectPath) {
        if (!(await requireBridge('fileSync'))) {
          setSyncStatus({ syncing: false, progress: 0 });
          return [];
        }

        const result = await desktopBridge.requestSyncCheck(
          project._id,
          projectPath,
          filteredCloudFiles
        );

        if (result.success && result.fileStatuses) {
          setSyncStatus({ syncing: false, progress: 100 });
          if (onSyncComplete) {
            onSyncComplete();
          }
          return result.fileStatuses;
        } else {
          console.error('Sync check failed:', result);
          throw new Error(result.error || 'Failed to check sync status');
        }
      }

      // Fallback: No sync available
      setSyncStatus({
        syncing: false,
        progress: 0,
        error: 'Sync not available. Please use the desktop app.',
      });
      return [];
    } catch (error) {
      console.error('Error checking status:', error);
      reportTransferError('Check sync status', error, {
        projectId: project._id,
        projectName: project.name,
        files: latestVersionFiles,
      });
      setSyncStatus({
        syncing: false,
        progress: 0,
        error: error instanceof Error ? error.message : 'Status check failed',
      });
      return [];
    }
  };

  const commitChanges = async (
    commitMessage: string,
    checked?:
      | { files: ProjectFileType[]; statuses: FileStatus[] }
      | (() => Promise<{
          files: ProjectFileType[];
          statuses: FileStatus[];
        } | null>)
  ): Promise<SyncOperationResult> => {
    const projectPath = getProjectPathValue();
    if (!projectPath && !projectDir) {
      return {
        success: false,
        error: 'No project directory selected',
      };
    }

    try {
      setSyncStatus({ syncing: true, progress: 0 });

      // Enqueue before any I/O. The queue, not the project page, owns scanning,
      // hashing and uploading, and opens the transfer sidebar immediately.
      await enqueueSync({
        projectId: project._id,
        projectName: project.name,
        files: [],
        statusMessage: 'Checking local changes',
        successMessage: 'Project synced successfully!',
        errorMessage: 'Project sync failed. Please try again.',
        run: async (callbacks) => {
          const abortSignal = callbacks.getAbortSignal?.();
          const checkCancelled = () => {
            if (abortSignal?.aborted)
              throw new DOMException('Transfer cancelled', 'AbortError');
          };
          checkCancelled();
          if (
            getRuntimeInfo().runtime === 'Electron' &&
            !electronAPI?.readFile
          ) {
            throw new Error(
              'The Electron native file API is unavailable. Restart the desktop app before creating a version. No version was created.'
            );
          }
          const snapshot =
            typeof checked === 'function' ? await checked() : checked;
          checkCancelled();
          if (snapshot === null)
            throw new Error(
              'Could not verify local changes. Retry the transfer.'
            );

          // Step 1: Check what files have changed
          const fileStatuses = snapshot?.statuses ?? (await checkStatus());
          checkCancelled();
          const baseFiles = snapshot?.files ?? getLatestVersionFiles();
          callbacks.onContext?.({ files: baseFiles });
          assertUniqueFilePaths(baseFiles);
          if (
            fileStatuses.some(
              (file) =>
                file.status === FileSyncStatus.UNKNOWN ||
                file.status === FileSyncStatus.BEHIND
            )
          ) {
            throw new Error(
              'Some files are unreadable or behind the cloud version. Review Sync before creating a version.'
            );
          }
          if (!fileStatuses || fileStatuses.length === 0) {
            setSyncStatus({ syncing: false, progress: 100 });
            throw new Error('No changes to commit');
          }

          const changedFiles = fileStatuses.filter(
            (f) => f.status !== FileSyncStatus.UP_TO_DATE
          );

          if (changedFiles.length === 0) {
            setSyncStatus({ syncing: false, progress: 100 });
            throw new Error('No changes to commit');
          }

          setSyncStatus({ syncing: true, progress: 10 });

          // Step 2: Prepare file metadata for new/changed files
          const filesToUpload = changedFiles.filter(
            (f) =>
              f.status === FileSyncStatus.NEW ||
              f.status === FileSyncStatus.NEWER
          );
          callbacks.onStatusMessage?.('Reading and hashing files');

          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const newFiles: any[] = [];
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const updatedFiles: any[] = [];
          const loadedFiles: File[] = [];
          const filesWithHashes: Array<{
            file: File;
            fullHash: string;
            fileName: string;
            size: number;
          }> = [];

          // Only proceed if we have Electron or desktop bridge (for file reading)
          if (!isElectron && !projectPath) {
            if (!(await requireBridge('commit'))) {
              throw new Error('Desktop app required');
            }
          }

          for (const fileStatus of filesToUpload) {
            checkCancelled();
            let localFile: File;
            try {
              const result = await getFileFromPathValue(fileStatus.fileName);
              if (!result) throw new Error('Native file reader is unavailable');
              localFile = result;
            } catch (cause) {
              callbacks.onDiagnostic?.('Read local file', cause, fileStatus);
              throw Object.assign(
                new Error(
                  `Could not read "${fileStatus.fileName}". No version was created.`
                ),
                { cause }
              );
            }

            loadedFiles.push(localFile);
            const contentType = getContentType(localFile);
            const { quickHash, fullHash } = await calculateHashes(localFile);
            filesWithHashes.push({
              file: localFile,
              fullHash,
              fileName: fileStatus.fileName,
              size: localFile.size,
            });

            const waveForm =
              getFileCategory(contentType, localFile.name) ===
              FileCategory.AUDIO
                ? await getWaveformData(localFile)
                : null;

            const fileMetadata = {
              fileName: fileStatus.fileName,
              contentType,
              size: localFile.size,
              quickHash,
              fullHash,
              waveForm: waveForm || null,
            };

            if (fileStatus.status === FileSyncStatus.NEW) {
              newFiles.push(fileMetadata);
            } else if (fileStatus.status === FileSyncStatus.NEWER) {
              // Find the existing file to replace
              const existingFile = baseFiles.find(
                (f) => f.fileName === fileStatus.fileName
              );
              updatedFiles.push({
                ...fileMetadata,
                replacesFileId: existingFile?._id,
              });
            }
          }

          setSyncStatus({ syncing: true, progress: 30 });

          const allDetectedPlugins = await detectPluginsFromFiles(loadedFiles);
          checkCancelled();

          // Step 3: Get deleted file IDs
          const deletedFiles = changedFiles.filter(
            (f) => f.status === FileSyncStatus.DELETED
          );
          const deletedFileIds = deletedFiles
            .map((df) => {
              const cloudFile = baseFiles.find(
                (cf) => cf.fileName === df.fileName
              );
              return cloudFile?._id;
            })
            .filter(Boolean) as string[];

          setSyncStatus({ syncing: true, progress: 50 });

          // Step 4+5: Push version and upload files
          callbacks.onFilesPrepared?.(
            filesWithHashes.map(({ fileName, size, fullHash }) => ({
              fileName,
              size,
              fullHash,
            }))
          );
          await pushVersionAndUpload({
            onContext: callbacks.onContext,
            onDiagnostic: callbacks.onDiagnostic,
            projectId: project._id,
            commitData: {
              commitMessage,
              newFiles,
              updatedFiles,
              deletedFileIds,
              plugins: allDetectedPlugins,
            },
            filesToUpload: filesWithHashes.map(
              ({ file, fullHash, fileName }) => ({
                file,
                fullHash,
                fileName,
              })
            ),
            pushMutation: projectVersionPush,
            finalizeMutation: projectVersionFinalize,
            onUploadError: async (versionId) => {
              try {
                await projectVersionRollback({
                  variables: {
                    projectId: project._id,
                    versionId,
                  },
                });
                console.log(
                  `[projectSync] Rolled back version ${versionId} for project ${project._id} after upload failure`
                );
              } catch (rollbackError) {
                callbacks.onDiagnostic?.(
                  'Rollback failed version',
                  rollbackError
                );
                console.error(
                  `Failed to rollback version ${versionId} for project ${project._id} after upload failure:`,
                  rollbackError
                );
              }
            },
            onProgress: (pct) =>
              setSyncStatus({
                syncing: true,
                progress: 50 + Math.round((pct * 40) / 100),
              }),
            onStatusMessage: callbacks.onStatusMessage,
            onFileStart: callbacks.onFileStart,
            onFileProgress: callbacks.onFileProgress,
            onFileComplete: callbacks.onFileComplete,
            onFileError: callbacks.onFileError,
            abortSignal,
          });
        },
      });

      setSyncStatus({ syncing: false, progress: 100 });

      if (onSyncComplete) {
        onSyncComplete();
      }

      return { success: true };
    } catch (error) {
      reportTransferError('Create version', error, {
        projectId: project._id,
        projectName: project.name,
        files:
          typeof checked === 'function'
            ? latestVersionFiles
            : (checked?.files ?? latestVersionFiles),
      });
      console.error('Commit error:', error);
      setSyncStatus({
        syncing: false,
        progress: 0,
        error: error instanceof Error ? error.message : 'Commit failed',
      });
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Commit failed',
      };
    }
  };

  return {
    syncStatus,
    checkStatus,
    commitChanges,
  };
}
